import { useEffect, useRef, useState, type PointerEvent } from "react";
import { cn } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import type {
  CatalogProvider,
  DiscoveryPlacement,
} from "../../api/integrations";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { ProviderLogo } from "../../pages/profile-settings/integrations-hub/provider-logos";
import { StatusDot } from "../../pages/profile-settings/integrations-hub/status-badge";
import { useTriggerSync } from "../../pages/profile-settings/integrations-hub/integrations-actions";
import { needsReconnect } from "../../pages/profile-settings/integrations-hub/connection-health";
import { useTrackEvent } from "../../libs/analytics";
import {
  connectionHealth,
  connectionLabel,
  INTEGRATIONS_PATH,
  primaryProvider,
} from "./model";

import asanaMark from "./logos/asana.image.svg";
import notionMark from "./logos/notion.image.svg";
import mondayMark from "./logos/monday.image.svg";
import clickupMark from "./logos/clickup.image.svg";
import googleDriveMark from "./logos/google-drive.image.svg";
import dropboxMark from "./logos/dropbox.image.svg";
import canvaMark from "./logos/canva.image.svg";

// Figma's unpadded product marks; other catalog providers retain the shared fallback.
const DISCOVERY_MARKS: Record<string, string> = {
  asana: asanaMark,
  notion: notionMark,
  monday: mondayMark,
  clickup: clickupMark,
  "google-drive": googleDriveMark,
  dropbox: dropboxMark,
  canva: canvaMark,
};
export function DiscoveryLogo({
  provider,
  compact = false,
  size = compact ? 15 : 20,
  className,
}: {
  provider: CatalogProvider;
  compact?: boolean;
  size?: 15 | 16 | 20;
  className?: string;
}) {
  const logoClassName = cn(
    "shrink-0 object-contain",
    size === 15 ? "size-[15px]" : size === 16 ? "size-4" : "size-5",
    className
  );
  return DISCOVERY_MARKS[provider.key] ? (
    <img src={DISCOVERY_MARKS[provider.key]} alt="" className={logoClassName} />
  ) : (
    <ProviderLogo
      providerKey={provider.key}
      displayName={provider.displayName}
      serverLogo={provider.logo}
      className={cn(logoClassName, "rounded-none")}
    />
  );
}

export function ProviderIcons({
  providers,
  limit = providers.length,
  boxed = false,
  compact = false,
  iconSize = compact ? 15 : 20,
  onActivate,
}: {
  providers: CatalogProvider[];
  limit?: number;
  boxed?: boolean;
  compact?: boolean;
  iconSize?: 15 | 16 | 20;
  onActivate?: (provider: CatalogProvider) => void;
}) {
  return (
    <span
      className="inline-flex flex-wrap items-center gap-2"
      aria-label={providers.map((p) => p.displayName).join(", ")}
    >
      {providers.slice(0, limit).map((provider) => {
        const iconClass = boxed
          ? `flex ${compact ? "size-6" : "size-8"} items-center justify-center rounded-lg border border-neutral-100 bg-white shadow-xs`
          : `flex ${iconSize === 16 ? "size-4" : iconSize === 15 ? "size-[15px]" : "size-5"} items-center justify-center`;
        return onActivate ? (
          <button
            key={provider.key}
            type="button"
            aria-label={`Set up ${provider.displayName} integration`}
            title={provider.displayName}
            className={`${iconClass} cursor-pointer p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
            onClick={() => onActivate(provider)}
          >
            <DiscoveryLogo provider={provider} size={iconSize} />
          </button>
        ) : (
          <span
            key={provider.key}
            title={provider.displayName}
            className={iconClass}
          >
            <DiscoveryLogo provider={provider} size={iconSize} />
          </span>
        );
      })}
      {providers.length > limit && (
        <span className="text-xs font-medium">+{providers.length - limit}</span>
      )}
    </span>
  );
}

export function IntegrationStatusRow({
  provider,
  canManage,
  placement,
  onNavigate,
}: {
  provider: CatalogProvider;
  canManage: boolean;
  placement: DiscoveryPlacement | "topnav";
  onNavigate?: () => void;
}) {
  const connection = provider.connection!;
  const sync = useTriggerSync(connection.id);
  const navigate = useNavigate();
  const track = useTrackEvent();
  const reconnect = needsReconnect(connection);
  const canSync =
    canManage &&
    connection.status === "active" &&
    !connection.activity &&
    connection.configuredCapabilities?.some(
      (cap) => cap.enabled && cap.capability.endsWith(":source")
    );
  const openManage = () => {
    if (reconnect)
      track({
        eventName: "integration-discovery-reconnect",
        metadata: { placement, provider: provider.key },
        useServices: ["AMP"],
      });
    navigate(`${INTEGRATIONS_PATH}/${provider.key}`);
    onNavigate?.();
  };
  return (
    <div className="space-y-2 rounded-lg bg-white p-3 font-inter text-neutral-900 shadow-xs">
      <button
        type="button"
        onClick={openManage}
        aria-label={`Open ${provider.displayName} integration`}
        className="block w-full space-y-2 rounded p-0 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <span className="flex items-center gap-2">
          <DiscoveryLogo provider={provider} />
          <span className="min-w-0 flex-1 text-sm font-medium">
            {provider.displayName}
          </span>
          <StatusDot {...connectionHealth(provider)} />
          <span className="text-xs">{connectionLabel(provider)}</span>
        </span>
        {connection.syncedTotals && (
          <span className="block text-xs text-neutral-600">
            {connection.syncedTotals.added.toLocaleString()} imported in the
            latest source syncs
          </span>
        )}
      </button>
      <div className="flex flex-wrap gap-2">
        {canSync && (
          <Button
            size="xs"
            variant="outline"
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            {sync.isPending ? "Starting…" : "Sync now"}
          </Button>
        )}
        <Button size="xs" variant="outline" onClick={openManage}>
          {reconnect && canManage
            ? "Reconnect"
            : connection.status === "pending_config" && canManage
              ? "Finish setup"
              : "Manage"}
          <i aria-hidden="true" className="fa-regular fa-arrow-right" />
        </Button>
      </div>
    </div>
  );
}

export function IntegrationStatus({
  providers,
  canManage,
  placement,
}: {
  providers: CatalogProvider[];
  canManage: boolean;
  placement: DiscoveryPlacement;
}) {
  const [openMode, setOpenMode] = useState<"hover" | "pinned" | null>(null);
  const [, setTick] = useState(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    const timer = setInterval(() => setTick((tick) => tick + 1), 60_000);
    return () => {
      clearInterval(timer);
      clearTimeout(closeTimer.current);
    };
  }, []);
  if (providers.length === 0) return null;
  const primary = primaryProvider(providers)!;
  const hover = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    clearTimeout(closeTimer.current);
    setOpenMode((mode) => mode ?? "hover");
  };
  const leave = (event: PointerEvent) => {
    if (event.pointerType !== "mouse") return;
    closeTimer.current = setTimeout(
      () => setOpenMode((mode) => (mode === "hover" ? null : mode)),
      200
    );
  };
  return (
    <Popover
      open={openMode !== null}
      onOpenChange={(next) => {
        clearTimeout(closeTimer.current);
        setOpenMode(next ? "pinned" : null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="max-w-full h-auto min-h-8 flex-wrap rounded-lg"
          onClick={(event) => {
            // Hover already opened the popover; the first click pins it instead of toggling it shut.
            event.preventDefault();
            clearTimeout(closeTimer.current);
            setOpenMode((mode) => (mode === "pinned" ? null : "pinned"));
          }}
          onPointerEnter={hover}
          onPointerLeave={leave}
          aria-label={`Integration status: ${providers.map((p) => `${p.displayName}, ${connectionLabel(p)}`).join("; ")}`}
        >
          <ProviderIcons providers={providers} limit={2} />
          <StatusDot {...connectionHealth(primary)} />
          <span className="text-xs">{connectionLabel(primary)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(360px,calc(100vw-32px))] space-y-2 bg-blue-25 p-3 font-inter"
        onPointerEnter={hover}
        onPointerLeave={leave}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <p className="text-sm font-semibold">Connected integrations</p>
        {providers.map((provider) => (
          <IntegrationStatusRow
            key={provider.key}
            provider={provider}
            canManage={canManage}
            placement={placement}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
}
